/**
 * MomentCard - Contextual cards for timely, relevant information
 * 
 * Examples:
 * - Weather: "It's going to rain at 3pm. Your outdoor meeting might be affected."
 * - Traffic: "Heavy traffic on your route. Leave 10 min early."
 * - Calendar: "You have 3 back-to-back meetings. Need a break scheduled?"
 * - Email: "5 emails need responses. Oldest from 2 days ago."
 * - Task: "3 tasks due today. 1 is marked high priority."
 * 
 * These appear in the main chat area based on context.
 */
import React, { useState } from 'react';
import { getIdToken } from '../services/firebase';
import {
  Cloud,
  Car,
  Calendar,
  Mail,
  CheckSquare,
  TrendingUp,
  AlertCircle,
  X,
  ChevronRight,
  Sparkles
} from './shared';

// =============================================================================
// TYPES
// =============================================================================

type MomentType = 
  | 'weather' 
  | 'traffic' 
  | 'calendar' 
  | 'email' 
  | 'tasks' 
  | 'insight' 
  | 'achievement';

interface MomentCardData {
  id: string;
  type: MomentType;
  title: string;
  description: string;
  icon?: React.ComponentType<{ className?: string }>;
  action?: {
    label: string;
    prompt: string;  // What to put in chat when clicked
  };
  data?: Record<string, any>;  // Type-specific data
  expiresAt?: Date;
  priority?: number;
}

// =============================================================================
// ICON MAPPING
// =============================================================================

const getMomentIcon = (type: MomentType) => {
  switch (type) {
    case 'weather':
      return Cloud;
    case 'traffic':
      return Car;
    case 'calendar':
      return Calendar;
    case 'email':
      return Mail;
    case 'tasks':
      return CheckSquare;
    case 'insight':
      return TrendingUp;
    case 'achievement':
      return Sparkles;
    default:
      return AlertCircle;
  }
};

const getMomentColors = (type: MomentType) => {
  switch (type) {
    case 'weather':
      return {
        bg: 'from-blue-500/20 to-cyan-500/20',
        border: 'border-blue-400/30',
        icon: 'text-blue-400'
      };
    case 'traffic':
      return {
        bg: 'from-amber-500/20 to-orange-500/20',
        border: 'border-amber-400/30',
        icon: 'text-amber-400'
      };
    case 'calendar':
      return {
        bg: 'from-purple-500/20 to-pink-500/20',
        border: 'border-purple-400/30',
        icon: 'text-purple-400'
      };
    case 'email':
      return {
        bg: 'from-red-500/20 to-rose-500/20',
        border: 'border-red-400/30',
        icon: 'text-red-400'
      };
    case 'tasks':
      return {
        bg: 'from-green-500/20 to-emerald-500/20',
        border: 'border-green-400/30',
        icon: 'text-green-400'
      };
    case 'achievement':
      return {
        bg: 'from-yellow-500/20 to-amber-500/20',
        border: 'border-yellow-400/30',
        icon: 'text-yellow-400'
      };
    default:
      return {
        bg: 'from-cyan-500/20 to-teal-500/20',
        border: 'border-cyan-400/30',
        icon: 'text-cyan-400'
      };
  }
};

// =============================================================================
// MOMENT CARD COMPONENT
// =============================================================================

interface MomentCardProps {
  moment: MomentCardData;
  onAction?: (prompt: string) => void;
  onDismiss?: (id: string) => void;
  compact?: boolean;
}

export const MomentCard: React.FC<MomentCardProps> = ({
  moment,
  onAction,
  onDismiss,
  compact = false
}) => {
  const Icon = moment.icon || getMomentIcon(moment.type);
  const colors = getMomentColors(moment.type);

  return (
    <div 
      className={`
        relative overflow-hidden
        bg-gradient-to-br ${colors.bg}
        backdrop-blur-xl
        border ${colors.border}
        rounded-2xl
        ${compact ? 'p-3' : 'p-4'}
        transition-all duration-200
        hover:scale-[1.02]
        hover:shadow-[0_0_30px_rgba(34,211,238,0.2)]
      `}
    >
      {/* Dismiss button */}
      {onDismiss && (
        <button
          onClick={() => onDismiss(moment.id)}
          className="absolute top-2 right-2 p-1 hover:bg-white/10 rounded-full transition-colors"
        >
          <X className="w-3 h-3 text-white/50 hover:text-white/80" />
        </button>
      )}

      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`p-2 rounded-xl bg-black/30 ${compact ? '' : 'mt-0.5'}`}>
          <Icon className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} ${colors.icon}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold text-white ${compact ? 'text-sm' : ''}`}>
            {moment.title}
          </h4>
          <p className={`text-gray-300 mt-1 ${compact ? 'text-xs' : 'text-sm'}`}>
            {moment.description}
          </p>

          {/* Action */}
          {moment.action && onAction && (
            <button
              onClick={() => onAction(moment.action!.prompt)}
              className={`
                mt-3 flex items-center gap-1.5
                text-cyan-400 hover:text-cyan-300
                ${compact ? 'text-xs' : 'text-sm'}
                font-medium
                transition-colors
              `}
            >
              {moment.action.label}
              <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MOMENT CARDS CONTAINER
// =============================================================================

interface MomentCardsProps {
  onAction?: (prompt: string) => void;
  maxCards?: number;
  layout?: 'horizontal' | 'vertical' | 'grid';
}

export const MomentCards: React.FC<MomentCardsProps> = ({
  onAction,
  maxCards = 3,
  layout = 'horizontal'
}) => {
  const [moments, setMoments] = useState<MomentCardData[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [_loading, setLoading] = useState(true);

  // Fetch real moments from API
  React.useEffect(() => {
    const fetchMoments = async () => {
      try {
        // Get user's location for weather
        let lat: number | undefined;
        let lon: number | undefined;
        
        if ('geolocation' in navigator) {
          try {
            const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
            });
            lat = pos.coords.latitude;
            lon = pos.coords.longitude;
          } catch {
            // Location not available, proceed without weather
          }
        }

        const API_BASE = import.meta.env.VITE_API_URL || 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';
        const params = new URLSearchParams();
        if (lat !== undefined) params.set('lat', lat.toString());
        if (lon !== undefined) params.set('lon', lon.toString());
        
        // Fetch from API with Firebase auth token
        const token = await getIdToken();
        if (!token) return;
        const response = await fetch(`${API_BASE}/api/moments?${params}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          const apiMoments: MomentCardData[] = (data.moments || []).map((m: any) => ({
            id: m.type + '-' + Math.random().toString(36).substr(2, 9),
            type: m.type,
            title: m.title,
            description: m.description,
            data: m,
            action: getActionForType(m.type)
          }));
          
          setMoments(apiMoments.filter(m => !dismissed.has(m.id)).slice(0, maxCards));
          setLoading(false);
          return;
        }
      } catch (error) {
        console.warn('Moments API unavailable, using contextual fallback');
      }

      // Fallback to context-aware mock moments
      const now = new Date();
      const hour = now.getHours();
      const contextMoments: MomentCardData[] = [];

      if (hour >= 6 && hour < 12) {
        contextMoments.push({
          id: 'cal-today',
          type: 'calendar',
          title: 'Check your day',
          description: 'See what\'s on your calendar',
          action: { label: 'See my day', prompt: "What's on my calendar today?" }
        });
      } else if (hour >= 12 && hour < 18) {
        contextMoments.push({
          id: 'email-pending',
          type: 'email',
          title: 'Check emails',
          description: 'See if anything needs attention',
          action: { label: 'Handle emails', prompt: "Show me emails that need responses" }
        });
      }

      contextMoments.push({
        id: 'tasks-due',
        type: 'tasks',
        title: 'Your tasks',
        description: 'See what needs to be done',
        action: { label: 'See tasks', prompt: "What tasks are due today?" }
      });

      setMoments(contextMoments.filter(m => !dismissed.has(m.id)).slice(0, maxCards));
      setLoading(false);
    };

    fetchMoments();
  }, [maxCards, dismissed]);

  // Generate action based on moment type
  const getActionForType = (type: string): { label: string; prompt: string } => {
    switch (type) {
      case 'calendar':
        return { label: 'See my day', prompt: "What's on my calendar today?" };
      case 'email':
        return { label: 'Handle emails', prompt: "Show me emails that need responses" };
      case 'tasks':
        return { label: 'See tasks', prompt: "What tasks are due today?" };
      case 'weather':
        return { label: 'Weather details', prompt: "What's the weather forecast for today?" };
      default:
        return { label: 'Learn more', prompt: `Tell me more about ${type}` };
    }
  };

  const handleDismiss = (id: string) => {
    setDismissed(prev => new Set([...prev, id]));
  };

  if (moments.length === 0) return null;

  const layoutClasses = {
    horizontal: 'flex gap-4 overflow-x-auto pb-2',
    vertical: 'flex flex-col gap-3',
    grid: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
  }[layout];

  return (
    <div className={`w-full max-w-4xl mx-auto ${layoutClasses}`}>
      {moments.map((moment) => (
        <div key={moment.id} className={layout === 'horizontal' ? 'min-w-[280px] flex-shrink-0' : ''}>
          <MomentCard
            moment={moment}
            onAction={onAction}
            onDismiss={handleDismiss}
            compact={layout === 'horizontal'}
          />
        </div>
      ))}
    </div>
  );
};

export default MomentCard;
